import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV, CONTACT, IMG } from "../data/site";
import { EASE } from "../lib/motion";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 border-b bg-warmwhite/90 backdrop-blur-md transition-all duration-500 ${
          scrolled ? "border-graphite/10 shadow-[0_1px_0_rgba(18,18,18,0.04)]" : "border-graphite/5"
        }`}
      >
        <nav className="u-container nav-container flex h-[70px] items-center justify-between md:h-[84px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center" aria-label="Casagon start">
            <img src={IMG.casagonLogo} alt="Casagon Bygg & Allservice AB" className="h-8 w-auto md:h-11" />
          </Link>

          <div className="hidden items-center gap-9 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `overline link-underline transition-colors ${
                    isActive ? "text-casared" : "text-graphite hover:text-graphite"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-phone"
              className="hidden items-center gap-2 text-sm font-medium text-graphite md:flex"
            >
              <Phone size={15} strokeWidth={2} />
              {CONTACT.phoneDisplay}
            </a>
            <Link
              to="/kontakt"
              data-testid="nav-quote-cta"
              className="hidden bg-graphite px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-warmwhite transition-colors duration-300 hover:bg-casared md:inline-block"
            >
              Begär offert
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Öppna meny"
              className="flex h-11 w-11 items-center justify-center border border-graphite/20 text-graphite lg:hidden"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col bg-graphite px-6 pt-28 pb-10 text-warmwhite lg:hidden"
          >
            <div className="flex flex-col">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, ease: EASE, duration: 0.6 }}
                  className="border-b border-warmwhite/10"
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-link-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                    className="block py-5 font-display text-3xl font-bold tracking-tight"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <div className="mt-auto flex flex-col gap-4">
              <Link
                to="/kontakt"
                data-testid="mobile-quote-cta"
                className="bg-casared py-4 text-center text-xs font-bold uppercase tracking-[0.2em] text-warmwhite"
              >
                Begär offert
              </Link>
              <a
                href={CONTACT.phoneHref}
                data-testid="mobile-phone"
                className="flex items-center justify-center gap-2 border border-warmwhite/25 py-4 text-sm font-medium"
              >
                <Phone size={16} /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
